/**
 * Badge im Header, das anzeigt auf wie viele Filialen der eingeloggte Nutzer
 * Zugriff hat. Klick öffnet ein kleines Panel mit der vollständigen Liste.
 *
 * Gezählt werden bewusst nur Filialen mit echten Statistik-Daten
 * (Zugriff ∩ Stores mit Daten, via useAvailableStores) — NICHT alle im Cockpit
 * als `active` geflaggten Stores. `data.accessible_store_count` aus /me zählt
 * auch "Karteileichen" (geschlossene/inaktive Filialen, die nie auf
 * active=FALSE gesetzt wurden) und weicht damit von den Berichts-Filtern ab.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type MeResponse } from '../lib/api.ts';
import { useAvailableStores } from '../lib/useAvailableStores.ts';
import { Icon } from './Icon.tsx';

export function AccessBadge() {
  const [open, setOpen] = useState(false);
  const { data } = useQuery<MeResponse>({
    queryKey: ['me'],
    queryFn: api.me,
    staleTime: 5 * 60_000,
  });
  // useAvailableStores braucht /dimensions; solange das lädt liefert es die
  // ungefilterte Liste. Bis dahin die Zahl unterdrücken, damit nicht kurz der
  // zu hohe Cockpit-Wert aufblitzt.
  const { isPending: dimsPending } = useQuery({
    queryKey: ['dimensions'],
    queryFn: api.dimensions,
  });
  const stores = useAvailableStores();

  if (!data) return null;

  const isFull = data.has_full_access;
  const isAdmin = data.role === 'admin';
  const count = stores.length;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium border transition-colors ${
          isFull
            ? 'bg-positive-500/10 text-positive-600 border-positive-500/30 hover:bg-positive-500/20'
            : 'bg-warning-500/10 text-warning-600 border-warning-500/30 hover:bg-warning-500/20'
        }`}
        title="Filial-Zugriff anzeigen"
      >
        <Icon name={isAdmin ? 'settings' : 'check'} size={12} />
        {isFull ? (
          <span>
            <span className="font-bold">{dimsPending ? '…' : count}</span> Filialen
            {isAdmin && <span className="opacity-60 ml-1">(Admin)</span>}
          </span>
        ) : (
          <span>
            <span className="font-bold">{dimsPending ? '…' : count}</span> von{' '}
            <span className="opacity-60">{data.total_active_stores}</span> Filialen
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-72 z-50 bg-white dark:bg-slate-900 rounded-xl shadow-elevated border border-slate-200 dark:border-slate-700 overflow-hidden animate-slide-up">
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">
              <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                Freigegebene Filialen
              </div>
              <div className="text-2xs text-slate-500 mt-0.5">
                {isAdmin
                  ? 'Du bist Admin und siehst alle Filialen mit Statistik-Daten.'
                  : `${count} von ${data.total_active_stores} Filialen sichtbar — die Statistik blendet die anderen automatisch aus.`}
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto scrollbar-thin">
              {stores.length === 0 ? (
                <div className="px-3 py-4 text-xs text-slate-400">
                  Keine Filialen mit Daten. Bitte den Administrator fragen.
                </div>
              ) : (
                <ul className="py-1">
                  {stores.map((s) => (
                    <li
                      key={s.id}
                      className="px-3 py-1 text-xs flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      <span className="text-2xs font-mono text-slate-400 w-10 shrink-0">
                        {s.store_number}
                      </span>
                      <span className="truncate">{s.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-800 text-2xs text-slate-500">
              Zugriff verwaltet der Cockpit-Admin unter{' '}
              <a
                href="/admin"
                className="text-brand-600 hover:underline"
              >
                Cockpit → Verwaltung
              </a>
              .
            </div>
          </div>
        </>
      )}
    </div>
  );
}
